import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { InvoiceEditorV2 } from '../components/invoice-v2/InvoiceEditorV2';
import RebillConfirmDialog from '../components/invoice/RebillConfirmDialog';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import { InvoiceV2State } from '../utils/invoiceV2PdfGenerator';
import { InvoiceStatus, UpdateInvoiceDto } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle, Send, Save } from 'lucide-react';
import HelpTooltip from '../components/ui/HelpTooltip';
import { useAuth } from '../contexts/AuthContext';


const InvoiceEditorV2Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState<Partial<InvoiceV2State>>({});
  const [dataKey, setDataKey] = useState(0); // Add key to force re-render
  const [showBilledItems, setShowBilledItems] = useState(false); // Toggle for showing billed items
  const [showRebillDialog, setShowRebillDialog] = useState(false); // Show rebill confirmation dialog
  const [rebillItem, setRebillItem] = useState<any>(null); // Item to be re-billed
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<any>(null); // Store current invoice for status checking
  const [isMarkAsSentModalOpen, setIsMarkAsSentModalOpen] = useState(false);
  const hasRunRef = useRef(false); // Prevent multiple runs

  // Check if invoice can be edited
  const canEdit = useMemo(() => {
    if (!currentInvoice) return true; // Loading or new invoice
    // Allow editing Draft, Sent, Partial, and Overdue invoices
    // Only lock Paid and Cancelled invoices
    return ['Draft', 'Sent', 'Partial', 'Overdue'].includes(currentInvoice.status);
  }, [currentInvoice]);

  const isReadOnly = !canEdit;

  useEffect(() => {
    // Prevent multiple runs
    if (hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;
    
    const loadInvoice = async () => {
      try {
        setIsLoading(true);
        
        if (id && id !== 'create') {
          // Load existing invoice
          try {
            // Clear ALL cached data first
            queryClient.clear();
            
            const invoice = await apiService.getInvoiceById(parseInt(id));

            // Store current invoice for status checking
            setCurrentInvoice(invoice);

            const logoBase64 = invoice.companyLogoBase64 || '';

            // Helper function to build complete address
            const buildCompleteAddress = (address: string, city: string, state: string, zipCode: string) => {
              const parts = [address, city, state, zipCode].filter(part => part && part.trim());
              return parts.join('\n');
            };

            const mappedData: Partial<InvoiceV2State> = {
              logo: logoBase64,
              companyName: invoice.companyName || '',
              companyAddress: buildCompleteAddress(
                invoice.companyAddress || '', 
                invoice.companyCity || '', 
                invoice.companyState || '', 
                invoice.companyZipCode || ''
              ),
              recipientName: invoice.clientName || '',
              recipientAddress: buildCompleteAddress(
                invoice.clientAddress || '', 
                invoice.clientCity || '', 
                invoice.clientState || '', 
                invoice.clientZipCode || ''
              ),
              recipientPhone: invoice.clientPhone || '',
              invoiceNumber: invoice.invoiceNumber || '',
              invoiceDate: new Date(invoice.invoiceDate),
              dueDate: new Date(invoice.dueDate),
              items: invoice.items.map(item => ({
                id: item.id.toString(),
                description: item.description,
                quantity: item.quantity,
                rate: item.rate,
                jobContactEntryId: item.jobContactEntryId // Link back to timeline item
              })),
              taxRate: invoice.taxRate,
              discountAmount: invoice.discountAmount || 0,
              discountType: invoice.discountType || 'None',
              discountPercentage: invoice.discountPercentage || 0,
              discountDescription: invoice.discountDescription || '',
              amountPaid: invoice.amountPaid || 0,
              paymentInfo: invoice.paymentInfo || '',
              terms: invoice.terms || '',
              notes: invoice.notes || ''
            };
            
            console.log('🔍 Mapped logo value:', mappedData.logo);
            console.log('🔍 Logo starts with data:', mappedData.logo?.startsWith('data:'));
            
            // Load all additional data first, then set everything at once
            let workOrderData: any = {};
            let companyData: Partial<InvoiceV2State> = {};
            
            // Load work order data if this invoice is associated with a work order
            if (invoice.workOrderId) {
              try {
                const workOrder = await apiService.getWorkOrder(invoice.workOrderId);
                console.log('Work order response:', workOrder);
                if (workOrder.success && workOrder.data) {
                  console.log('Work order data:', workOrder.data);
                  workOrderData = {
                    recipientName: workOrder.data.clientName || '',
                    recipientAddress: buildCompleteAddress(
                      workOrder.data.clientAddress || '', 
                      workOrder.data.clientCity || '', 
                      workOrder.data.clientState || '', 
                      workOrder.data.clientZipCode || ''
                    ),
                    recipientPhone: workOrder.data.clientPhone || '',
                    recipientEmail: workOrder.data.clientEmail || ''
                    // Don't override notes with work order description
                  };
            
                  // Don't reload items from timeline when editing existing invoice
                  // The invoice already has its items from the database (mappedData.items)
                  // Reloading would overwrite user's edits and show all timeline items again
                  
                  // If invoice tax rate is 0 or null, try to get it from client location
                  if ((!invoice.taxRate || invoice.taxRate === 0) && workOrder.data.clientLocationId) {
                    try {
                      const clientLocations = await apiService.getClientLocations(workOrder.data.clientId);
                      if (clientLocations.success && clientLocations.data) {
                        const clientLocation = clientLocations.data.find((loc: any) => loc.id === workOrder.data.clientLocationId);
                        if (clientLocation && clientLocation.taxRate !== null && clientLocation.taxRate !== undefined) {
                          mappedData.taxRate = clientLocation.taxRate;
                          console.log('Updated tax rate from client location:', clientLocation.taxRate);
                        }
                      }
                    } catch (error) {
                      console.log('Could not fetch client location tax rate:', error);
                    }
                  }
                  
                  console.log('Work order data loaded successfully');
                }
              } catch (error) {
                console.log('Work order data not available');
              }
            }
            
            // Load company data
            try {
              console.log('Loading company data for existing invoice...');
              const companyResponse = await apiService.getCompany();
              console.log('Company response for existing invoice:', companyResponse);
              
              const company = companyResponse?.data || companyResponse;
              
              // Temporary fallback for testing - SAME AS OLD EDIT PAGE
              const fallbackCompany = {
                name: 'Aline Facilities',
                address: '2200 Polo Park Drive',
                city: 'Dayton',
                state: 'OH',
                zipCode: '45439'
              };
              
              const finalCompany = company || fallbackCompany;
              
              const logoPath = company?.logoUrl || 'company_1/logos/e62d7e0b-94b4-416d-919a-d43d88243ff6.jpeg';
              
              // Construct full logo URL like the old edit page does
              const fullLogoUrl = logoPath.startsWith('data:') ? logoPath : `${window.location.protocol}//${window.location.hostname}:5269/api/companies/logo/${logoPath}`;
              
              // Construct full address like old edit page
              const companyAddress2 = `${finalCompany?.city || ''}, ${finalCompany?.state || ''} ${finalCompany?.zipCode || ''}`.trim().replace(/^,\s*/, '');
              const fullCompanyAddress = `${finalCompany?.address || 'Your Address'}\n${companyAddress2}`.trim();
              
              companyData = {
                logo: fullLogoUrl,
                companyName: finalCompany?.name || 'Your Company',
                companyAddress: fullCompanyAddress,
                companyPhone: finalCompany?.phone || '',
                companyEmail: finalCompany?.email || '',
                companyWebsite: finalCompany?.website || ''
                // Don't override invoice-specific fields with company defaults
              };
              
              console.log('Company data loaded successfully');
            } catch (error) {
              console.log('Company data not available for existing invoice, using defaults');
            }
            
            // Set all data at once to prevent multiple re-renders
            setInvoiceData({ ...mappedData, ...workOrderData, ...companyData });
            setDataKey(prev => prev + 1); // Force re-render
          } catch (error) {
            console.error('Error loading invoice:', error);
            toast.error('Failed to load invoice');
            navigate('/invoices');
          }
        } else {
          // Create new invoice - load all available data
          const defaultInvoiceData: Partial<InvoiceV2State> = {
            logo: '',
            companyName: 'Your Company Name',
            companyAddress: 'Your Company Address',
            companyPhone: '',
            companyEmail: '',
            companyWebsite: '',
            recipientName: 'New Customer',
            recipientAddress: '',
            recipientPhone: '',
            recipientEmail: '',
            invoiceNumber: `INV-${Date.now()}`,
            invoiceDate: new Date(),
            dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            items: [{ id: '1', description: 'Service Item', quantity: 1, rate: 0 }],
            taxRate: 0.10,
            discountAmount: 0,
            discountType: 'None',
            discountPercentage: 0,
            amountPaid: 0,
            paymentInfo: '',
            terms: '',
            notes: ''
          };
          
          setInvoiceData(defaultInvoiceData);
          
                  // Automatically load company data for new invoices - COPY FROM OLD EDIT PAGE
                  try {
                    console.log('Loading company data for new invoice...');
                    const companyResponse = await apiService.getCompany();
                    console.log('Company response:', companyResponse);
                    
                    const company = companyResponse?.data || companyResponse; // Handle both possible structures
                    console.log('Company data:', companyResponse);
                    console.log('Company:', company);
                    console.log('Company name:', company?.name);
                    console.log('Company logoUrl:', company?.logoUrl);
                    
                    // Temporary fallback for testing - SAME AS OLD EDIT PAGE
                    const fallbackCompany = {
                      name: 'Aline Facilities',
                      address: '2200 Polo Park Drive',
                      city: 'Dayton',
                      state: 'OH',
                      zipCode: '45439'
                    };
                    
                    const finalCompany = company || fallbackCompany;
                    
                    const logoPath = company?.logoUrl || 'company_1/logos/e62d7e0b-94b4-416d-919a-d43d88243ff6.jpeg';
                    console.log('Setting logo path to:', logoPath);
                    
                    // Construct full logo URL like the old edit page does
                    const fullLogoUrl = logoPath.startsWith('data:') ? logoPath : `${window.location.protocol}//${window.location.hostname}:5269/api/companies/logo/${logoPath}`;
                    console.log('Full logo URL:', fullLogoUrl);
                    
                    // Construct full address like old edit page
                    const companyAddress2 = `${finalCompany?.city || ''}, ${finalCompany?.state || ''} ${finalCompany?.zipCode || ''}`.trim().replace(/^,\s*/, '');
                    const fullCompanyAddress = `${finalCompany?.address || 'Your Address'}\n${companyAddress2}`.trim();
                    
                    const companyData: Partial<InvoiceV2State> = {
                      logo: fullLogoUrl, // Use full URL like old edit page
                      companyName: finalCompany?.name || 'Your Company',
                      companyAddress: fullCompanyAddress, // Full address with city, state, zip
                      companyPhone: finalCompany?.phone || '',
                      companyEmail: finalCompany?.email || '',
                      companyWebsite: finalCompany?.website || ''
                      // Don't override invoice-specific fields with company defaults
                    };
                    
                    console.log('Auto-loaded company data:', companyData);
                    setInvoiceData(prev => ({ ...prev, ...companyData }));
                  } catch (error) {
                    console.log('Company data not available, using defaults');
                  }
          
          
          // Try to load work order data if workOrderId is provided
          const workOrderId = new URLSearchParams(location.search).get('workOrderId');
          if (workOrderId) {
            try {
              const workOrder = await apiService.getWorkOrder(parseInt(workOrderId));
              console.log('Work order response for new invoice:', workOrder);
              if (workOrder.success && workOrder.data) {
                        console.log('Work order data for new invoice:', workOrder.data);
                        const workOrderData: any = {
                          recipientName: workOrder.data.clientName || '',
                          recipientAddress: workOrder.data.clientAddress || '',
                          recipientPhone: workOrder.data.clientPhone || '',
                          recipientEmail: workOrder.data.clientEmail || '',
                          notes: workOrder.data.description || ''
                        };
                
                // Get billable items from timeline using the dedicated API
                try {
                  console.log('Attempting to load billable items for new invoice work order:', workOrderId);
                  const billableItemsResponse = await apiService.getBillableItemsFromTimeline(parseInt(workOrderId));
                  console.log('Billable items response for new invoice:', billableItemsResponse);
                  console.log('Billable items response type for new invoice:', typeof billableItemsResponse);
                  console.log('Billable items response is array for new invoice:', Array.isArray(billableItemsResponse));
                  
                  // Handle direct array response (the API returns the array directly)
                  if (Array.isArray(billableItemsResponse) && billableItemsResponse.length > 0) {
                    console.log('Raw billable items data for new invoice:', billableItemsResponse);
                    console.log('First item details:', billableItemsResponse[0]);
                    console.log('First item isBilled:', billableItemsResponse[0]?.isBilled);
                    console.log('First item invoiceIds:', billableItemsResponse[0]?.invoiceIds);

                    // Filter based on toggle state (smart filtering)
                    const filteredItems = showBilledItems
                      ? billableItemsResponse
                      : billableItemsResponse.filter(item => !item.isBilled);
                    console.log('Filtered items:', filteredItems);
                    console.log('Total billable items:', billableItemsResponse.length);
                    console.log('Already billed items:', billableItemsResponse.length - filteredItems.length);
                    console.log('Show billed items:', showBilledItems);

                    // Debug: log each item's billing status
                    billableItemsResponse.forEach((item, idx) => {
                      console.log(`Item ${idx}: isBilled=${item.isBilled}, invoiceIds=${JSON.stringify(item.invoiceIds)}, desc="${item.description}"`);
                    });
                    
                    const billableItems = filteredItems.map((item, index) => ({
                      id: `timeline-${item.jobContactEntryId}`,
                      description: item.description || `Work Order Item ${index + 1}`,
                      quantity: item.quantity || 1,
                      rate: item.rate || 0,
                      jobContactEntryId: item.jobContactEntryId // Link back to timeline item
                    }));
                    
                    console.log('Mapped billable items for new invoice:', billableItems);
                    console.log('Default invoice items before billable:', defaultInvoiceData.items);
                    if (billableItems.length > 0) {
                      // Replace default items with billable items for new invoices from work orders
                      workOrderData.items = billableItems;
                      console.log('Final workOrderData.items for new invoice (replaced with billable):', workOrderData.items);
                    }
                  }
                } catch (error) {
                  console.error('Could not load billable items from timeline for new invoice:', error);
                }
                
                console.log('About to set invoice data with workOrderData for new invoice:', workOrderData);
                console.log('Current invoiceData before update for new invoice:', invoiceData);
                
                setInvoiceData(prev => {
                  const newData = { ...prev, ...workOrderData };
                  console.log('New invoice data after merge for new invoice:', newData);
                  return newData;
                });
                
                        setDataKey(prev => prev + 1); // Force re-render
                        console.log('Data key updated for new invoice, component should re-render');
              }
            } catch (error) {
              console.log('Work order data not available');
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error in loadInvoice:', error);
        toast.error('Failed to load invoice data');
      } finally {
        setIsLoading(false);
      }
    };

    loadInvoice();
  }, [id, navigate]); // Remove location.search to prevent re-runs

  const handleManualSave = async (data: InvoiceV2State) => {
    try {
      // Validate user has companyId
      if (!user?.companyId) {
        toast.error('Unable to determine company. Please log in again.');
        return;
      }

      // Get company data for currency code
      let currencyCode = 'USD'; // Default fallback
      try {
        const companyResponse = await apiService.getCompany();
        if (companyResponse?.success && companyResponse?.data?.currencyCode) {
          currencyCode = companyResponse.data.currencyCode;
        }
      } catch (error) {
        console.warn('Could not fetch company currency code, using USD default:', error);
      }

      const subtotal = data.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
      const calculatedDiscountAmount = data.discountType === 'Percentage' && data.discountPercentage
        ? subtotal * (data.discountPercentage / 100)
        : data.discountAmount;
      const subtotalAfterDiscount = subtotal - calculatedDiscountAmount;
      const taxAmount = subtotalAfterDiscount * data.taxRate;
      const total = subtotalAfterDiscount + taxAmount;

      // Get workOrderId from URL if available
      const workOrderId = new URLSearchParams(location.search).get('workOrderId');

      const invoiceDto = {
        companyId: user.companyId,
        workOrderId: workOrderId ? parseInt(workOrderId) : undefined,
        invoiceNumber: data.invoiceNumber || `INV-${Date.now()}`,
        invoiceDate: data.invoiceDate || new Date(),
        dueDate: data.dueDate || new Date(),
        customerName: data.recipientName || 'New Customer',
        customerAddress: data.recipientAddress || '',
        customerPhone: data.recipientPhone || '',
        subtotal: subtotal,
        taxRate: data.taxRate,
        taxAmount: taxAmount,
        discountAmount: calculatedDiscountAmount,
        discountType: data.discountType,
        discountPercentage: data.discountPercentage,
        total: total,
        currencyCode: currencyCode,
        notes: data.notes,
        terms: data.terms,
        paymentInfo: data.paymentInfo,
        status: InvoiceStatus.Draft,
        customItems: data.items.map((item, index) => ({
          description: item.description || `Item ${index + 1}`,
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          amount: (item.quantity || 1) * (item.rate || 0),
          sortOrder: index + 1,
          jobContactEntryId: item.jobContactEntryId // Link back to timeline item
        }))
      };

      const newInvoice = await apiService.createInvoice(invoiceDto);

      // Refresh invoices list and navigate to the created invoice
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice created successfully');

      // Navigate to the newly created invoice
      navigate(`/invoices/${newInvoice.id}/edit`);
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice');
    }
  };

  const handleManualSaveClick = async () => {
    if (isManualSaving || !invoiceData) return;

    setIsManualSaving(true);
    try {
      // Get company data for currency code
      let currencyCode = 'USD'; // Default fallback
      try {
        const companyResponse = await apiService.getCompany();
        if (companyResponse?.success && companyResponse?.data?.currencyCode) {
          currencyCode = companyResponse.data.currencyCode;
        }
      } catch (error) {
        console.warn('Could not fetch company currency code, using USD default:', error);
      }

      const subtotal = (invoiceData.items || []).reduce((sum, item) => sum + (item.quantity * item.rate), 0);
      const calculatedDiscountAmount = invoiceData.discountType === 'Percentage' && invoiceData.discountPercentage
        ? subtotal * (invoiceData.discountPercentage / 100)
        : invoiceData.discountAmount;
      const subtotalAfterDiscount = subtotal - calculatedDiscountAmount;
      const taxAmount = subtotalAfterDiscount * (invoiceData.taxRate || 0);
      const total = subtotalAfterDiscount + taxAmount;

      const invoiceDto: UpdateInvoiceDto = {
        id: parseInt(id!),
        invoiceDate: invoiceData.invoiceDate?.toISOString?.() || new Date().toISOString(),
        dueDate: invoiceData.dueDate?.toISOString?.() || new Date().toISOString(),
        subtotal: subtotal,
        taxRate: invoiceData.taxRate,
        taxAmount: taxAmount,
        discountAmount: calculatedDiscountAmount,
        discountType: invoiceData.discountType,
        discountPercentage: invoiceData.discountPercentage,
        discountDescription: invoiceData.discountDescription,
        total: total,
        currencyCode: currencyCode,
        notes: invoiceData.notes,
        terms: invoiceData.terms,
        paymentInfo: invoiceData.paymentInfo,
        status: invoiceData.status || InvoiceStatus.Draft,
        items: (invoiceData.items || []).map((item, index) => {
          // Determine if this is a real database ID (small number < 1000000)
          // vs a temporary ID (timeline-xxx, timestamp like 1234567890123, etc)
          let itemId: number | undefined = undefined;
          if (typeof item.id === 'string') {
            if (!item.id.startsWith('timeline-') && !isNaN(Number(item.id))) {
              const numericId = parseInt(item.id);
              // Only use as ID if it's a reasonable database ID (< 1 million)
              if (numericId < 1000000) {
                itemId = numericId;
              }
            }
          } else if (typeof item.id === 'number' && item.id < 1000000) {
            itemId = item.id;
          }

          return {
            id: itemId,
            description: item.description || `Item ${index + 1}`,
            quantity: item.quantity || 1,
            rate: item.rate || 0,
            amount: (item.quantity || 1) * (item.rate || 0),
            sortOrder: index + 1,
            jobContactEntryId: item.jobContactEntryId
          };
        })
      };

      await apiService.updateInvoice(parseInt(id!), invoiceDto);

      // Update local state with the calculated discount amount so PDF generation has the correct value
      setInvoiceData(prev => ({
        ...prev,
        discountAmount: calculatedDiscountAmount
      }));

      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      toast.success('Invoice saved successfully');
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setIsManualSaving(false);
    }
  };

  const handleMarkAsSent = () => {
    if (!currentInvoice?.id) return;
    setIsMarkAsSentModalOpen(true);
  };

  const confirmMarkAsSent = async () => {
    if (!currentInvoice?.id) return;

    try {
      await apiService.markInvoiceAsSent(currentInvoice.id);
      toast.success('Invoice marked as sent');
      setIsMarkAsSentModalOpen(false);
      
      // Refresh the invoice data to show updated status
      queryClient.invalidateQueries({ queryKey: ['invoice', currentInvoice.id] });
      
      // Manually reload the invoice data to update the UI
      const updatedInvoice = await apiService.getInvoiceById(currentInvoice.id);
      setCurrentInvoice(updatedInvoice);
    } catch (error) {
      console.error('Error marking invoice as sent:', error);
      toast.error('Failed to mark invoice as sent');
    }
  };

  // Reload billable items when toggle state changes
  useEffect(() => {
    if (id === 'create' && showBilledItems !== undefined) {
      // Trigger a reload of billable items with new filter state
      const workOrderId = new URLSearchParams(location.search).get('workOrderId');
      if (workOrderId) {
        loadBillableItems(parseInt(workOrderId));
      }
    }
  }, [showBilledItems]);

  const loadBillableItems = async (workOrderId: number) => {
    try {
      console.log('Loading billable items for work order:', workOrderId, 'Show billed:', showBilledItems);
      const billableItemsResponse = await apiService.getBillableItemsFromTimeline(workOrderId);
      
      if (Array.isArray(billableItemsResponse) && billableItemsResponse.length > 0) {
        // Filter based on toggle state
        const filteredItems = showBilledItems 
          ? billableItemsResponse 
          : billableItemsResponse.filter(item => !item.isBilled);
        
        const billableItems = filteredItems.map((item, index) => ({
          id: `timeline-${item.jobContactEntryId}`,
          description: item.description || `Work Order Item ${index + 1}`,
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          jobContactEntryId: item.jobContactEntryId
        }));
        
        if (billableItems.length > 0) {
          setInvoiceData(prev => ({ ...prev, items: billableItems }));
          setDataKey(prev => prev + 1); // Force re-render
        }
      }
    } catch (error) {
      console.error('Error loading billable items:', error);
    }
  };

  const handleRebillConfirm = () => {
    if (rebillItem) {
      // Add the billed item to the invoice
      const newItem = {
        id: `timeline-${rebillItem.jobContactEntryId}`,
        description: rebillItem.description || 'Billable Item',
        quantity: rebillItem.quantity || 1,
        rate: rebillItem.rate || 0,
        jobContactEntryId: rebillItem.jobContactEntryId
      };
      
      setInvoiceData(prev => ({
        ...prev,
        items: [...(prev.items || []), newItem]
      }));
      setDataKey(prev => prev + 1); // Force re-render
      
      toast.success('Billed item added to invoice');
    }
    setRebillItem(null);
  };

  const showRebillItemSelector = async (workOrderId: number) => {
    try {
      const billableItemsResponse = await apiService.getBillableItemsFromTimeline(workOrderId);
      
      if (Array.isArray(billableItemsResponse) && billableItemsResponse.length > 0) {
        // Find billed items
        const billedItems = billableItemsResponse.filter(item => item.isBilled);
        
        if (billedItems.length > 0) {
          // For now, show the first billed item as an example
          // In a real implementation, you'd show a list for the user to select from
          const itemToRebill = billedItems[0];
          setRebillItem(itemToRebill);
          setShowRebillDialog(true);
        } else {
          toast.error('No billed items found to re-bill');
        }
      }
    } catch (error) {
      console.error('Error loading billed items:', error);
      toast.error('Failed to load billed items');
    }
  };

  const handleLoadCompanyData = async () => {
    try {
      const companyResponse = await apiService.getCompany();
      console.log('Company response:', companyResponse);

      const company = companyResponse?.data || companyResponse; // Handle both possible structures
      console.log('Company data:', companyResponse);
      console.log('Company:', company);
      console.log('Company name:', company?.name);
      console.log('Company logoUrl:', company?.logoUrl);

      // Temporary fallback for testing - SAME AS OLD EDIT PAGE
      const fallbackCompany = {
        name: 'Aline Facilities',
        address: '2200 Polo Park Drive',
        city: 'Dayton',
        state: 'OH',
        zipCode: '45439'
      };

      const finalCompany = company || fallbackCompany;

      const logoPath = company?.logoUrl || 'company_1/logos/e62d7e0b-94b4-416d-919a-d43d88243ff6.jpeg';
      console.log('Setting logo path to:', logoPath);

      // Construct full logo URL like the old edit page does
      const fullLogoUrl = logoPath.startsWith('data:') ? logoPath : `${window.location.protocol}//${window.location.hostname}:5269/api/companies/logo/${logoPath}`;
      console.log('Full logo URL:', fullLogoUrl);

      // Construct full address like old edit page
      const companyAddress2 = `${finalCompany?.city || ''}, ${finalCompany?.state || ''} ${finalCompany?.zipCode || ''}`.trim().replace(/^,\s*/, '');
      const fullCompanyAddress = `${finalCompany?.address || 'Your Address'}\n${companyAddress2}`.trim();

      const companyData: Partial<InvoiceV2State> = {
        logo: fullLogoUrl, // Use full URL like old edit page
        companyName: finalCompany?.name || 'Your Company',
        companyAddress: fullCompanyAddress, // Full address with city, state, zip
        companyPhone: finalCompany?.phone || '',
        companyEmail: finalCompany?.email || '',
        companyWebsite: finalCompany?.website || ''
        // Don't override invoice-specific fields with company defaults
      };

      console.log('Loaded company data:', companyData);
      setInvoiceData(prev => ({ ...prev, ...companyData }));
    } catch (error) {
      console.error('Error loading company settings:', error);
      toast.error('Failed to load company data');
    }
  };

  // Memoized onChange handler to prevent re-render loops
  const handleInvoiceChange = useCallback((data: InvoiceV2State) => {
    setInvoiceData(data);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Show warning banner if invoice is locked or needs attention */}
      {currentInvoice && !canEdit && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Invoice Locked for Editing
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                This invoice has status "{currentInvoice.status}" and cannot be edited.
                Only Draft, Sent, Partial, and Overdue invoices can be modified.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/invoices')}
                  className="text-sm font-medium text-red-800 dark:text-red-200 hover:text-red-600 dark:hover:text-red-400"
                >
                  ← Back to Invoices
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show workflow banner for overdue invoices */}
      {currentInvoice && currentInvoice.status === 'Overdue' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-orange-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Overdue Invoice - Action Required
                <HelpTooltip 
                  content="Overdue invoices can now be edited to update terms, record payments, or create credit memos. This allows you to actively manage collections and resolve outstanding balances."
                  title="New: Overdue Invoice Management"
                  position="auto"
                  className="ml-2"
                />
              </h3>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-2">
                This invoice is overdue. Consider taking action to resolve the outstanding balance.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(`/payments?invoiceId=${currentInvoice.id}`)}
                  className="inline-flex items-center px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-md text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-800 hover:bg-orange-200 dark:hover:bg-orange-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Record Payment
                  <HelpTooltip 
                    content="Log any payments received from the client, including partial payments. This will update the invoice status and payment history."
                    position="auto"
                    className="ml-2"
                  />
                </button>
                <button
                  onClick={() => navigate(`/credit-memos/create?invoiceId=${currentInvoice.id}`)}
                  className="inline-flex items-center px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-md text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-800 hover:bg-orange-200 dark:hover:bg-orange-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Create Credit Memo
                </button>
                <button
                  onClick={() => {
                    // TODO: Implement send reminder email functionality
                    toast.success('Reminder email functionality coming soon!');
                  }}
                  className="inline-flex items-center px-3 py-2 border border-orange-300 dark:border-orange-600 rounded-md text-sm font-medium text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-800 hover:bg-orange-200 dark:hover:bg-orange-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show info banner for sent/partial invoices */}
      {currentInvoice && ['Sent', 'Partial'].includes(currentInvoice.status) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-blue-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Invoice {currentInvoice.status === 'Sent' ? 'Sent' : 'Partially Paid'}
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                {currentInvoice.status === 'Sent' 
                  ? 'This invoice has been sent to the client and is awaiting payment.'
                  : 'This invoice has received partial payment. You can record additional payments or create credit memos.'
                }
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(`/payments?invoiceId=${currentInvoice.id}`)}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Record Payment
                </button>
                {currentInvoice.status === 'Partial' && (
                  <button
                    onClick={() => navigate(`/credit-memos/create?invoiceId=${currentInvoice.id}`)}
                    className="inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Create Credit Memo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle for showing billed items */}
      {id === 'create' && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900">Create Invoice</h2>
              <span className="text-sm text-gray-500">from Work Order Timeline</span>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={showBilledItems}
                  onChange={(e) => setShowBilledItems(e.target.checked)}
                  className="rounded border-gray-300 text-accent-color focus:ring-accent-color"
                />
                <span className="text-gray-700">Show already billed items</span>
              </label>
              
              {showBilledItems && (
                <button
                  onClick={() => {
                    // Show dialog to select a billed item to re-bill
                    const workOrderId = new URLSearchParams(location.search).get('workOrderId');
                    if (workOrderId) {
                      showRebillItemSelector(parseInt(workOrderId));
                    }
                  }}
                  className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                >
                  Add Billed Item
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mark as Sent button for draft invoices */}
      {id !== 'create' && currentInvoice?.status === 'Draft' && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900">Edit Invoice</h2>
              <span className="text-sm text-gray-500">{currentInvoice.invoiceNumber}</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleManualSaveClick}
                disabled={isManualSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {isManualSaving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Invoice
                  </>
                )}
              </button>

              <button
                onClick={handleMarkAsSent}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" />
                Mark as Sent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status indicator for sent invoices */}
      {id !== 'create' && currentInvoice?.status === 'Sent' && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900">Invoice Details</h2>
              <span className="text-sm text-gray-500">{currentInvoice.invoiceNumber}</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Invoice Sent</span>
            </div>
          </div>
        </div>
      )}
      
      <InvoiceEditorV2
                key={dataKey}
                initialData={invoiceData}
                onManualSave={id === 'create' ? handleManualSave : undefined}
                onChange={handleInvoiceChange}
                onLoadCompanyData={id === 'create' ? handleLoadCompanyData : undefined}
                isLoading={isLoading}
                isNewInvoice={id === 'create'}
                forceUpdateItems={true}
                isReadOnly={isReadOnly}
                isSaving={isManualSaving}
              />
      
      {/* Rebill Confirmation Dialog */}
      <RebillConfirmDialog
        isOpen={showRebillDialog}
        onClose={() => {
          setShowRebillDialog(false);
          setRebillItem(null);
        }}
        onConfirm={handleRebillConfirm}
        item={rebillItem || { description: '', invoiceIds: [] }}
      />

      {/* Mark as Sent Confirmation Modal */}
      <ConfirmDialog
        isOpen={isMarkAsSentModalOpen}
        onClose={() => setIsMarkAsSentModalOpen(false)}
        onConfirm={confirmMarkAsSent}
        title="Mark Invoice as Sent"
        message="Mark this invoice as sent? You will not be able to edit it after this action."
        confirmText="Mark as Sent"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
};

export default InvoiceEditorV2Page;